import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthenticationSandbox } from '@app/authentication/sandboxes/authentication.sandbox';
import { MoverRole } from '@app/move/enums/mover-role.enum';
import { UiOfferType } from '@app/move/enums/ui-offer-type.enum';
import { MoveSandbox } from '@app/move/sandboxes/move.sandbox';
import { RealEstateGroup } from '@app/real-estate-group/interfaces/real-estate-group.interface';
import { RealEstateGroupSandbox } from '@app/real-estate-group/sandboxes/real-estate-group.sandbox';
import { ServiceType } from '@app/services/types/service-type';
import { PosthogGroupTypes } from '@app/tracking/enums/posthog.enum';
import { MoveDateUtils } from '@app/tracking/utils/move-date.utils';
import { PosthogUtils } from '@app/tracking/utils/posthog.utils';
import { TransactionStore } from '@app/transaction/store/transaction.store';
import { AuthUser, DbUtils, Me, PostHogEventsEnum, PosthogFrom, Role } from '@smooved/core';
import posthog from 'posthog-js';
import { firstValueFrom, zip } from 'rxjs';
import { take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class PosthogService {
    private readonly route = inject(ActivatedRoute);
    private readonly transactionStore = inject(TransactionStore);

    constructor(
        private readonly realEstateGroupSandbox: RealEstateGroupSandbox,
        private readonly authenticationSandbox: AuthenticationSandbox,
        private readonly moveSandbox: MoveSandbox,
    ) {}

    public identify(id: string, user: AuthUser, extraProperties = {}): void {
        posthog.identify(id, {
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            ...extraProperties,
        });
    }

    public resetIdentity(): void {
        posthog.reset();
    }

    public groupByCompany(isAdmin: boolean, realEstateGroup?: RealEstateGroup): void {
        if (isAdmin) {
            this.group(PosthogGroupTypes.Company, 'admin');
            return;
        }
        this.group(PosthogGroupTypes.Company, DbUtils.getStringId(realEstateGroup), {
            name: realEstateGroup.name,
        });
    }

    public groupByOffice(officeId: string, realEstateGroup: RealEstateGroup): void {
        if (!officeId) return;
        const officeName = realEstateGroup.offices.find((office) => DbUtils.getStringId(office) === officeId)?.name;
        this.group(PosthogGroupTypes.Office, officeId, { name: officeName });
    }

    private group(type: PosthogGroupTypes, id: string, properties?: Object): void {
        posthog.group(type, id, properties);
    }

    /**
     * @deprecated Use sendEventForTransaction instead and explicitly pass the transactionId
     */
    public sendEventForTransactionState(event: PostHogEventsEnum, extraProperties = {}, error?: Error): void {
        if (!event) return;

        const transactionId = this.transactionStore.transactionId();

        if (transactionId) {
            this.sendEventForTransaction(event, transactionId, extraProperties, error);
        } else {
            this.moveSandbox.moveOnce$.subscribe((move) => {
                if (move?.movingDate) {
                    this.sendEventForTransaction(event, DbUtils.getStringId(move), extraProperties, error);
                } else {
                    this.sendEvent(event, extraProperties, error);
                }
            });
        }
    }

    /**
     * Sends an event for a specific transaction and enriches the event with move date properties from transaction store or move sandbox.
     *
     * @param event The event to send
     * @param transactionId The ID of the transaction
     * @param extraProperties Additional properties to add to the event
     * @param error Optional error to add to the event
     *
     */
    public async sendEventForTransaction(
        event: PostHogEventsEnum,
        transactionId: string,
        extraProperties = {},
        error?: Error,
    ): Promise<void> {
        if (!event) return;
        const moveDateProperties = await this.getMoveDatePropertiesFromState();
        this.sendEvent(event, { ...moveDateProperties, ...extraProperties, transactionId }, error);
    }

    /**
     * Gets move date properties from transaction store or move sandbox
     * @returns Promise that resolves to object with movingDate and daysToMoveDate properties or null if no valid data found
     */
    private async getMoveDatePropertiesFromState(): Promise<{ movingDate?: string; daysToMoveDate?: number } | null> {
        const transaction = this.transactionStore.transaction();
        if (transaction?.movingDate) {
            return this.createMoveDateProperties(transaction.movingDate);
        } else {
            // Fallback to move sandbox if transaction store doesn't have the data
            const move = await firstValueFrom(this.moveSandbox.moveOnce$);
            if (move?.movingDate) {
                return this.createMoveDateProperties(move.movingDate);
            }
            return null;
        }
    }

    /**
     * Creates move date properties for PostHog tracking
     * @param movingDate The move date as a Date object or ISO string
     * @returns Object with movingDate and daysToMoveDate properties
     */
    private createMoveDateProperties(movingDate: Date | string): { movingDate?: string; daysToMoveDate?: number } {
        const formattedMovingDate = MoveDateUtils.formatMovingDateForTracking(movingDate);
        const daysToMoveDate = MoveDateUtils.getDaysToMoveDate(movingDate);

        const properties: { movingDate?: string; daysToMoveDate?: number } = {};

        if (formattedMovingDate) {
            properties.movingDate = formattedMovingDate;
        }

        if (daysToMoveDate !== null) {
            properties.daysToMoveDate = daysToMoveDate;
        }
        return properties;
    }

    public sendEvent(event: PostHogEventsEnum, extra = {}, error?: Error): void {
        if (!event) return;
        zip(this.realEstateGroupSandbox.realEstateGroupOnce$, this.authenticationSandbox.authorizationOnce$).subscribe(
            ([realEstateGroup, authorization]) => {
                const properties: any = {
                    utmSource: this.utmSourceFactory(),
                    from: this.fromFactory(authorization),
                    role: authorization?.role,
                    realEstateGroupId: DbUtils.getStringId(realEstateGroup),
                    officeId: (realEstateGroup as unknown as Me)?.officeId,
                    realEstateAgentId: (realEstateGroup as unknown as Me)?.realEstateAgentId,
                    result: !!error ? 'Failure' : 'Success',
                    ...extra,
                };
                if (authorization?.role === Role.Mover) properties.moverType = authorization?.moverRole;
                if (error) properties.errorMessage = PosthogUtils.getErrorMessage(error);
                posthog.capture(event, properties);
            },
        );
    }

    public clickContactUs(contactType: string): void {
        if (!contactType) return;
        this.authenticationSandbox.authorizationOnce$.subscribe((auth) => {
            posthog.capture(PostHogEventsEnum.ClickContactUs, {
                utmSource: this.utmSourceFactory(),
                from: this.fromFactory(auth),
                contactType,
            });
        });
    }

    /**
     * Since we are still using the shopping cart mechanism right now, this function will loop over the items and send to Posthog
     * @private
     */
    public sendForShoppingCart(event: PostHogEventsEnum): void {
        this.moveSandbox.shoppingCart$.pipe(take(1)).subscribe((shoppingCart) => {
            const hasEnergy = shoppingCart?.some((item) => item.type === UiOfferType.Energy);
            const hasTelecom = shoppingCart?.some((item) => item.type === UiOfferType.Telecom);
            const transactionId = this.transactionStore.transaction()?.id;
            if (hasEnergy && hasTelecom) {
                this.sendEventForTransaction(event, transactionId, { currentService: ServiceType.EnergyNewContractAndTelecomNewContract });
            } else if (hasEnergy) {
                this.sendEventForTransaction(event, transactionId, { currentService: ServiceType.EnergyNewContract });
            } else if (hasTelecom) {
                this.sendEventForTransaction(event, transactionId, { currentService: ServiceType.TelecomNewContract });
            }
        });
    }

    private utmSourceFactory(): string {
        return this.route.snapshot.queryParamMap.get('utm_source');
    }

    private fromFactory(auth: AuthUser): string {
        const utmFrom = this.route.snapshot.queryParamMap.get('utm_medium');
        return utmFrom || this.getFromBasedOnAuthorization(auth) || 'App';
    }

    private getFromBasedOnAuthorization(auth: AuthUser): PosthogFrom {
        switch (auth?.role) {
            case Role.Admin:
                if (auth.realEstateAgentId) break;
                return PosthogFrom.Admin;
            case Role.RealEstateAgent:
                return PosthogFrom.RealEstateAgentDashboard;
            case Role.Mover:
                if (auth.moverRole === MoverRole.Transferee) return PosthogFrom.TransfereeDashboard;
                if (auth.moverRole === MoverRole.Leaver) return PosthogFrom.LeaverDashboard;
                break;
            default:
                return null;
        }
    }
}
